/**
 * Inline SVG icons used by the widget.
 *
 * The Klientele mark matches the favicon — a clean geometric "DF" monogram.
 * Stroke-only icons follow Lucide's 24×24 / 1.5 stroke convention so they
 * pair visually with shadcn/ui icons in the host app's surrounding UI.
 *
 * `currentColor` is used for fill/stroke so the icon inherits its color
 * from the launcher button's text color (--df-color-fg in styles.ts).
 */

/**
 * Default launcher icon — Klientele K monogram.
 *
 * The K letterform sourced from .claude/knowledge/KLIENTELE_BRAND_KIT.md.
 * Single-color stem + arms drawn with `currentColor` so the icon adapts to
 * the launcher / header surface it sits on. The intersection dot uses the
 * Klientele accent blue (#2563EB) — the brand's "one moment of color".
 */
export const KLIENTELE_MARK_SVG = `<svg viewBox="0 0 28 28" width="20" height="20" fill="none" aria-hidden="true">
  <rect x="7" y="6" width="2.5" height="16" fill="currentColor" rx="0.4"/>
  <line x1="9.5" y1="14" x2="20" y2="6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
  <line x1="9.5" y1="14" x2="20" y2="22" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
  <circle cx="9.5" cy="14" r="1.6" fill="#2563EB"/>
</svg>`;

/** @deprecated kept for backwards-compat; use KLIENTELE_MARK_SVG. */
export const DEVFLOW_MARK_SVG = KLIENTELE_MARK_SVG;

/** Modal close icon. */
export const CLOSE_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18" aria-hidden="true">
  <line x1="18" y1="6" x2="6" y2="18"></line>
  <line x1="6" y1="6" x2="18" y2="18"></line>
</svg>`;

/** Success checkmark — replaces the previous text "✓" with a stroke icon. */
export const CHECK_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" width="40" height="40" aria-hidden="true">
  <polyline points="20 6 9 17 4 12"></polyline>
</svg>`;

/** Send/paper-plane icon used inside the submit button. */
export const SEND_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true">
  <line x1="22" y1="2" x2="11" y2="13"></line>
  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
</svg>`;

/** Camera icon — used for the "Screenshot" attach button. */
export const CAMERA_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true">
  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
  <circle cx="12" cy="13" r="4"></circle>
</svg>`;

/** Video icon — used for the "Record screen" attach button. */
export const VIDEO_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true">
  <polygon points="23 7 16 12 23 17 23 7"></polygon>
  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
</svg>`;

/** Paperclip — file attach (also the mobile fallback for capture). */
export const PAPERCLIP_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true">
  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
</svg>`;

/** Trash — used to remove a queued attachment from the preview grid. */
export const TRASH_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true">
  <polyline points="3 6 5 6 21 6"></polyline>
  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
</svg>`;

/** Stop — small filled square shown on the active recording bar. */
export const STOP_SVG = `<svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12" aria-hidden="true">
  <rect x="6" y="6" width="12" height="12" rx="1"></rect>
</svg>`;

/** Generic file icon for non-image / non-video previews. */
export const FILE_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="22" height="22" aria-hidden="true">
  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
  <polyline points="14 2 14 8 20 8"></polyline>
</svg>`;

/** Pencil — opens the annotation editor on a screenshot thumbnail. */
export const PENCIL_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="12" height="12" aria-hidden="true">
  <path d="M12 20h9"></path>
  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
</svg>`;

/** Annotator toolbar icons */
export const PEN_TOOL_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true">
  <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
  <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
  <line x1="2" y1="2" x2="9.586" y2="9.586"></line>
</svg>`;

export const RECT_TOOL_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true">
  <rect x="3" y="3" width="18" height="18" rx="2"></rect>
</svg>`;

export const ARROW_TOOL_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true">
  <line x1="5" y1="12" x2="19" y2="12"></line>
  <polyline points="12 5 19 12 12 19"></polyline>
</svg>`;

export const UNDO_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true">
  <path d="M3 7v6h6"></path>
  <path d="M21 17a9 9 0 0 0-15-6.7L3 13"></path>
</svg>`;

export const TEXT_TOOL_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="14" height="14" aria-hidden="true">
  <polyline points="4 7 4 4 20 4 20 7"></polyline>
  <line x1="9" y1="20" x2="15" y2="20"></line>
  <line x1="12" y1="4" x2="12" y2="20"></line>
</svg>`;

/** Loading spinner — circular arc, spun via CSS animation `df-spin`. */
export const SPINNER_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" width="14" height="14" aria-hidden="true" class="df-spinner">
  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
</svg>`;

/** Composer toolbar (FR-2026-0105) — 16px, matching the mock's stroke set. */
const tool = (body: string) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16" aria-hidden="true">${body}</svg>`;
export const MIC_SVG = tool('<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/>');
export const TOOL_CAMERA_SVG = tool('<path d="M4 7h3l2-3h6l2 3h3v12H4z"/><circle cx="12" cy="13" r="3.5"/>');
export const TOOL_VIDEO_SVG = tool('<rect x="3" y="6" width="13" height="12" rx="2"/><path d="M16 10l5-3v10l-5-3"/>');
export const TOOL_CLIP_SVG = tool('<path d="M21 11l-8.5 8.5a5 5 0 0 1-7-7L14 4a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L15 7"/>');
export const ARROW_SVG = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>`;
export const PLAY_SVG = `<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true"><path d="M8 5.5v13l11-6.5z"/></svg>`;
export const PAUSE_SVG = `<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>`;
export const REDO_SVG = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3-6.7L21 8"/><path d="M21 3v5h-5"/></svg>`;
export const X_SMALL_SVG = `<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>`;
export const TICK_SVG = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>`;

/**
 * Render either the configured custom icon URL (as <img>) or the default
 * Klientele mark (inline SVG). Custom URLs render through <img> with strict
 * sizing and decoding hint; alt is empty since the launcher button has its own aria-label.
 */
export function renderLauncherIcon(iconUrl: string | null): string {
  if (iconUrl) {
    return `<img src="${escapeAttr(iconUrl)}" alt="" width="20" height="20" decoding="async" loading="eager" style="display:block;width:20px;height:20px;object-fit:contain;" />`;
  }
  return KLIENTELE_MARK_SVG;
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
